import { useCallback, useEffect, useRef, useState } from "react";
import { toApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { ErrorBanner, Loading } from "../../../components/Shell";
import type { TopBarSpec } from "../../../components/TopBar";
import { hasMessage, useT } from "../../../i18n";
import type { ConsoleApi } from "../api/consoleApi";
import type {
  ConsoleRoom,
  ScriptAction,
  ScriptInfo,
  ScriptState,
  ScriptTemplateKey,
} from "../types";
import { NoScript } from "../api/consoleApi";
import { Menu } from "../../../components/ui/Menu";
import { defaultTemplates, minutesOf, mmss, SCRIPT_TEMPLATE_KEYS } from "../lib/script";
import { StudentScriptBar } from "./StudentScriptBar";

/** 스크립트 상태를 다시 읽는 간격(남은 시간 표시 · 다른 창의 조작 반영) */
export const SCRIPT_POLL_MS = 1000;

/**
 * FS-13-01 마무리 스크립트 진행(SCR v0.6 sfs-13 · SR-TCH-03) — 콘솔 탭 틀 안(/teacher/room/:roomId/script).
 *  - 템플릿 고르기(관람형 · 도전형 · 협동형 — 펼쳐 고르기 · 진행 중에는 잠김) · 활동의 템플릿이 있으면 그것부터
 *  - 단계 표(✓ 지난 · ▶ 지금 강조 · 모드 · 시간/남은 시간) · 지금 행에 '다음 단계 →'(남은 0 이면 강조 — 자동으로 넘기지 않는다)
 *  - 시작 · 건너뛰기 · 일시정지/다시 · 끝내기 — 단계가 바뀌면 제어 평면이 그 단계 모드로 전환을 자동 발행한다
 *  - 아래 '학생 화면 미리보기' = FS-13-02 학생 상단 바와 같은 조각
 * 상단 바: '📋 마무리 스크립트 · 관람형' + 'n단계 / 전체'.
 */
export function ScriptPanel({
  room,
  api,
  onTop,
  onError,
  onChanged,
}: {
  room: ConsoleRoom;
  api: ConsoleApi;
  onTop: (spec: TopBarSpec | null) => void;
  onError: (e: unknown) => void;
  onChanged: () => void;
}) {
  const t = useT();
  const [info, setInfo] = useState<ScriptInfo | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<ScriptTemplateKey | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const inflight = useRef(false);

  const refresh = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      setInfo(await api.script());
      setLoadErr(null);
    } catch (e) {
      setLoadErr(toApiError(e).code);
    } finally {
      inflight.current = false;
    }
  }, [api]);
  useEffect(() => {
    void refresh();
    const h = window.setInterval(() => void refresh(), SCRIPT_POLL_MS);
    return () => window.clearInterval(h);
  }, [refresh]);

  const state = info?.state ?? null;
  const active = !!state && !state.done;
  const templates = info?.templates.length ? info.templates : defaultTemplates();
  const chosen: ScriptTemplateKey =
    (active ? state.template : null) ?? picked ?? info?.activityTemplate ?? "view";
  const tpl = templates.find((x) => x.key === chosen) ?? templates[0]!;
  const steps = active || state?.done ? state!.steps : tpl.steps;
  const tplName = t(`script.template.${active || state?.done ? state!.template : chosen}`);

  // 상단 바 — 패널이 정한다
  const topKey = JSON.stringify([tplName, active ? state.index : null, steps.length, state?.done]);
  useEffect(() => {
    onTop({
      icon: "📋",
      title: t("script.topTitle", { template: tplName }),
      chips: [
        {
          label: active
            ? t("script.chipStep", { n: state.index + 1, total: steps.length })
            : state?.done
              ? t("script.chipDone")
              : t("script.chipReady"),
          testId: "script-step-chip",
        },
      ],
    });
    // 스크립트 값이 바뀔 때만(topKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topKey, onTop, t]);
  useEffect(() => () => onTop(null), [onTop]);

  const act = async (key: string, a: ScriptAction) => {
    setBusy(key);
    try {
      const next = await api.scriptAction(a);
      setInfo((i) => (i ? { ...i, state: next } : i));
      onChanged();
    } catch (e) {
      // 다른 창에서 끝냈다 — 다시 읽는다
      if (e instanceof NoScript) void refresh();
      else onError(e);
    } finally {
      setBusy(null);
    }
  };

  if (loadErr && !info)
    return (
      <ErrorBanner
        cause={t("script.loadFailed")}
        action={t("teacher.room.loadFailed")}
        code={loadErr}
      />
    );
  if (!info) return <Loading />;

  const prewarm = room.state === "prewarm";
  const modeOf = (key: string, modeId: string) =>
    hasMessage(`script.modeOf.${key}`)
      ? t(`script.modeOf.${key}`)
      : hasMessage(`teacher.room.modeName.${modeId}`)
        ? t(`teacher.room.modeName.${modeId}`)
        : modeId;
  const due = active && state.remainingS <= 0;
  const last = active && state.index === state.steps.length - 1;
  const preview: ScriptState = active
    ? state
    : {
        template: chosen,
        steps,
        index: 0,
        remainingS: steps[0]?.seconds ?? 0,
        running: true,
        done: false,
      };

  return (
    <>
      <section
        className="pf-pane pf-pane--hero pf-script"
        aria-labelledby="script-title"
        data-testid="script-panel"
        data-state={active ? "run" : state?.done ? "done" : "pick"}
      >
        <h2 id="script-title" className="pf-h3">
          {t("script.progress")}
        </h2>
        <div className="pf-row">
          <Menu
            testId="script-template"
            ariaLabel={t("script.templateLabel")}
            disabled={active || busy !== null}
            label={<>{t("script.templatePick", { name: t(`script.template.${chosen}`) })} ▾</>}
            items={SCRIPT_TEMPLATE_KEYS.filter((k) => templates.some((x) => x.key === k)).map(
              (k) => ({
                id: k,
                on: k === chosen,
                label: (
                  <>
                    {t(`script.template.${k}`)}
                    {k === info.activityTemplate && (
                      <span className="pf-chip-static pf-chip-static--ok">
                        {t("script.activityDefault")}
                      </span>
                    )}
                  </>
                ),
              }),
            )}
            onPick={(k) => setPicked(k as ScriptTemplateKey)}
          />
          <span className="pf-muted">{t("script.templates")}</span>
        </div>
        <div className="pf-tbl-scroll">
          <table className="pf-tbl pf-script__tbl" data-testid="script-steps">
            <thead>
              <tr>
                <th scope="col" className="pf-script__mark">
                  <span className="pf-sr-only">{t("script.colState")}</span>
                </th>
                <th scope="col">{t("script.colStep")}</th>
                <th scope="col">{t("script.colMode")}</th>
                <th scope="col">{t("script.colTime")}</th>
                <th scope="col" className="pf-script__act">
                  <span className="pf-sr-only">{t("script.colAct")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {steps.map((s, i) => {
                const st = state?.done
                  ? "done"
                  : active
                    ? i < state.index
                      ? "done"
                      : i === state.index
                        ? "now"
                        : "next"
                    : "next";
                return (
                  <tr
                    key={`${s.key}-${i}`}
                    data-step={s.key}
                    data-state={st}
                    aria-current={st === "now" ? "step" : undefined}
                    className={st === "now" ? "pf-script__now" : undefined}
                  >
                    <td className="pf-script__mark" aria-hidden="true">
                      {st === "done" ? "✓" : st === "now" ? "▶" : ""}
                    </td>
                    <td>
                      <b>
                        <span aria-hidden="true">{s.icon} </span>
                        {t(`script.step.${s.key}`)}
                      </b>
                    </td>
                    <td>{modeOf(s.key, s.modeId)}</td>
                    <td data-testid={st === "now" ? "script-left" : undefined}>
                      {st === "now"
                        ? state!.remainingS > 0
                          ? t("script.timeLeft", {
                              min: minutesOf(s.seconds),
                              left: mmss(state!.remainingS),
                            })
                          : t("script.timeUp", { min: minutesOf(s.seconds) })
                        : t("teacher.home.minutes", { n: minutesOf(s.seconds) })}
                      {st === "now" && !state!.running && (
                        <span className="pf-chip-static">{t("script.pausedChip")}</span>
                      )}
                    </td>
                    <td className="pf-script__act">
                      {st === "now" && (
                        <Button
                          variant="accent"
                          className={`pf-btn--sm pf-script__next${due ? " pf-script__next--due" : ""}`}
                          disabled={busy !== null}
                          data-testid="script-next"
                          data-due={due || undefined}
                          onClick={() => void act("next", { action: "next" })}
                        >
                          {t(last ? "script.finish" : "script.next")}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="pf-row pf-row--wrap">
          {!active ? (
            <Button
              variant="green"
              className="pf-btn--sm"
              disabled={prewarm || busy !== null}
              data-testid="script-start"
              onClick={() => void act("start", { action: "start", template: chosen })}
            >
              {t(state?.done ? "script.restart" : "script.start")}
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                className="pf-btn--sm"
                disabled={busy !== null}
                data-testid="script-skip"
                onClick={() => void act("skip", { action: "skip" })}
              >
                {t("script.skip")}
              </Button>
              <Button
                variant="ghost"
                className="pf-btn--sm"
                disabled={busy !== null}
                data-testid={state.running ? "script-pause" : "script-resume"}
                onClick={() => void act("pause", { action: state.running ? "pause" : "resume" })}
              >
                {t(state.running ? "script.pause" : "script.resume")}
              </Button>
              <Button
                variant="ghost"
                className="pf-btn--sm"
                disabled={busy !== null}
                data-testid="script-stop"
                onClick={() => void act("stop", { action: "stop" })}
              >
                {t("script.stop")}
              </Button>
            </>
          )}
          <span className="pf-muted">{t("script.autoMode")}</span>
        </div>
        {state?.done && (
          <p className="pf-muted" role="status" data-testid="script-done">
            {t("script.doneNote")}
          </p>
        )}
      </section>
      <section
        className="pf-pane pf-pane--dim"
        aria-labelledby="script-preview-title"
        data-testid="fs-13-02-preview"
      >
        <h2 id="script-preview-title" className="pf-h3">
          {t("script.previewTitle")}
        </h2>
        <StudentScriptBar
          script={preview}
          leftS={preview.remainingS}
          se={false}
          testId="script-preview"
        />
        <p className="pf-muted">{t("script.previewNote")}</p>
      </section>
    </>
  );
}
